import React, { useState, useEffect, useCallback } from 'react';
import { useWallet } from '@solana/wallet-adapter-react';
import {
  ArrowLeft, Trophy, Crown, Target, Gamepad2,
  DollarSign, Award, Gem, Edit3, Check, X, Share2
} from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import axios from 'axios';

import { BADGE_ICONS, RARITY_STYLES, formatAddr } from './profile/constants';
import OverviewTab from './profile/OverviewTab';
import BadgesTab from './profile/BadgesTab';
import GamesTab from './profile/GamesTab';
import TournamentsTab from './profile/TournamentsTab';
import BettingTab from './profile/BettingTab';
import CollectionTab from './profile/CollectionTab';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { id: 'overview', label: 'Overview', icon: Gamepad2 },
  { id: 'badges', label: 'Badges', icon: Award },
  { id: 'games', label: 'Games', icon: Target },
  { id: 'tournaments', label: 'Tournaments', icon: Trophy },
  { id: 'betting', label: 'Betting', icon: DollarSign },
  { id: 'collection', label: 'Collection', icon: Gem },
];

const PlayerProfile = ({ playerId, onBack }) => {
  const { publicKey } = useWallet();
  const [profile, setProfile] = useState(null);
  const [achievements, setAchievements] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [editBio, setEditBio] = useState('');
  const [activeTab, setActiveTab] = useState('overview');

  const isOwnProfile = publicKey?.toBase58() === playerId;

  const fetchProfile = useCallback(async () => {
    try {
      const [profRes, achRes] = await Promise.all([
        axios.get(`${API}/profile/${playerId}`),
        axios.get(`${API}/player/achievements/${playerId}`).catch(() => ({ data: null })),
      ]);
      setProfile(profRes.data);
      setEditName(profRes.data.profile?.display_name || '');
      setEditBio(profRes.data.profile?.bio || '');
      if (achRes.data) setAchievements(achRes.data);
    } catch (e) {
      console.error('Error fetching profile:', e);
    } finally { setLoading(false); }
  }, [playerId]);

  useEffect(() => { fetchProfile(); }, [fetchProfile]);

  const saveProfile = async () => {
    try {
      await axios.post(`${API}/profile/${playerId}/update`, {
        display_name: editName,
        bio: editBio
      });
      toast.success('Profile updated!');
      setEditing(false);
      fetchProfile();
    } catch (e) {
      toast.error('Failed to update profile');
    }
  };

  const handleShareProfile = async () => {
    const url = `${window.location.origin}/profile/${encodeURIComponent(playerId)}`;
    const name = profile?.profile?.display_name || formatAddr(playerId);
    try {
      // Native share on mobile, clipboard fallback elsewhere
      if (navigator.share) {
        await navigator.share({
          title: `${name} on Metal Lego`,
          text: `Check out ${name}'s profile on Metal Lego!`,
          url,
        });
        return;
      }
      await navigator.clipboard.writeText(url);
      toast.success('Profile link copied to clipboard!');
    } catch (e) {
      // User canceled native share or clipboard failed — try a manual textarea fallback
      try {
        const ta = document.createElement('textarea');
        ta.value = url;
        ta.setAttribute('readonly', '');
        ta.style.position = 'absolute';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        toast.success('Profile link copied!');
      } catch (e2) {
        toast.error('Could not copy link. URL: ' + url);
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="animate-spin w-12 h-12 border-4 border-[#14F195] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center">
          <p className="text-[#FF4444] font-game text-xl mb-4">Profile not found</p>
          <Button onClick={onBack} className="game-btn"><ArrowLeft className="w-4 h-4 mr-2" /> Back</Button>
        </div>
      </div>
    );
  }

  const { game_stats, mode_stats, recent_scores, tournament_results, championships, bet_stats, owned_nfts, owned_skins } = profile;
  const displayName = profile.profile?.display_name || formatAddr(playerId);

  const badgeAchievements = (achievements?.achievements || []).filter(a => a.category === 'badge');
  const unlockedBadges = badgeAchievements.filter(b => b.unlocked);
  const lockedBadges = badgeAchievements.filter(b => !b.unlocked);

  return (
    <div className="min-h-screen bg-[#050505] p-4 md:p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <Button onClick={onBack} variant="ghost" className="text-[#A1A1AA] hover:text-white" data-testid="profile-back-btn">
            <ArrowLeft className="w-5 h-5 mr-2" /> Back
          </Button>
          <Button
            onClick={handleShareProfile}
            variant="ghost"
            className="text-[#14F195] hover:text-white hover:bg-[#14F195]/10 border border-[#14F195]/30"
            data-testid="share-profile-btn">
            <Share2 className="w-4 h-4 mr-2" /> Share
          </Button>
        </div>

        {/* Profile Card */}
        <div className="glass-card p-6 mb-6 border border-[#333]" data-testid="profile-card">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              {/* Avatar */}
              <div className="w-16 h-16 rounded-xl flex items-center justify-center text-2xl font-game"
                style={{
                  background: 'linear-gradient(135deg, #9945FF 0%, #14F195 100%)',
                  color: '#fff'
                }}>
                {displayName.charAt(0).toUpperCase()}
              </div>
              <div>
                {editing ? (
                  <div className="space-y-2">
                    <input type="text" value={editName} onChange={e => setEditName(e.target.value)} maxLength={30}
                      className="bg-[#1a1a1a] border border-[#333] rounded px-3 py-1 text-white text-sm focus:outline-none focus:border-[#14F195]"
                      placeholder="Display name" data-testid="edit-name-input" />
                    <input type="text" value={editBio} onChange={e => setEditBio(e.target.value)} maxLength={200}
                      className="bg-[#1a1a1a] border border-[#333] rounded px-3 py-1 text-white text-xs w-full focus:outline-none focus:border-[#14F195]"
                      placeholder="Bio" data-testid="edit-bio-input" />
                    <div className="flex gap-2">
                      <Button onClick={saveProfile} className="bg-[#14F195] text-black text-xs px-3 py-1"><Check className="w-3 h-3 mr-1" /> Save</Button>
                      <Button onClick={() => setEditing(false)} className="bg-[#333] text-xs px-3 py-1"><X className="w-3 h-3 mr-1" /> Cancel</Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <h1 className="font-game text-xl text-white flex items-center gap-2">
                      {displayName}
                      {championships > 0 && <Crown className="w-5 h-5 text-[#FFD700]" />}
                    </h1>
                    <p className="text-xs text-[#A1A1AA] font-mono">{formatAddr(playerId)}</p>
                    {profile.profile?.bio && <p className="text-sm text-[#888] mt-1">{profile.profile.bio}</p>}
                  </>
                )}
              </div>
            </div>
            {isOwnProfile && !editing && (
              <Button onClick={() => setEditing(true)} variant="ghost" className="text-[#A1A1AA] hover:text-white" data-testid="edit-profile-btn">
                <Edit3 className="w-4 h-4" />
              </Button>
            )}
          </div>

          {/* Quick Stats Row */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mt-6">
            <div className="bg-[#111] rounded-lg p-3 text-center border border-[#222]">
              <p className="text-[10px] text-[#666]">GAMES</p>
              <p className="font-game text-lg text-[#14F195]">{game_stats.total_games}</p>
            </div>
            <div className="bg-[#111] rounded-lg p-3 text-center border border-[#222]">
              <p className="text-[10px] text-[#666]">HIGH SCORE</p>
              <p className="font-game text-lg text-[#FFD700]">{game_stats.highest_score?.toLocaleString()}</p>
            </div>
            <div className="bg-[#111] rounded-lg p-3 text-center border border-[#222]">
              <p className="text-[10px] text-[#666]">KILLS</p>
              <p className="font-game text-lg text-[#FF3B30]">{game_stats.total_kills}</p>
            </div>
            <div className="bg-[#111] rounded-lg p-3 text-center border border-[#222]">
              <p className="text-[10px] text-[#666]">BEST LVL</p>
              <p className="font-game text-lg text-[#9945FF]">{game_stats.highest_level}</p>
            </div>
            <div className="bg-[#111] rounded-lg p-3 text-center border border-[#222]">
              <p className="text-[10px] text-[#666]">TITLES</p>
              <p className="font-game text-lg text-[#FFD700]">{championships}</p>
            </div>
          </div>

          {/* Badge preview row */}
          {unlockedBadges.length > 0 && (
            <div className="mt-5 pt-5 border-t border-[#222]" data-testid="profile-badge-preview">
              <div className="flex items-center justify-between mb-3">
                <p className="text-[10px] text-[#666] font-game flex items-center gap-1.5">
                  <Award className="w-3.5 h-3.5 text-[#FFD700]" />
                  BADGES EARNED
                  <span className="text-[#14F195]">{achievements.badge_count}/{achievements.total_badges}</span>
                </p>
                <button onClick={() => setActiveTab('badges')}
                  className="text-[10px] text-[#14F195] hover:text-white font-game"
                  data-testid="view-all-badges-btn">
                  VIEW ALL →
                </button>
              </div>
              <div className="flex gap-2 flex-wrap">
                {unlockedBadges.slice(0, 8).map(b => {
                  const Icon = BADGE_ICONS[b.icon] || Award;
                  const rarity = RARITY_STYLES[b.rarity] || RARITY_STYLES.common;
                  return (
                    <div key={b.id} title={`${b.name} — ${b.description}`}
                      className="w-11 h-11 rounded-lg flex items-center justify-center border-2 transition-transform hover:scale-110"
                      style={{
                        borderColor: rarity.color,
                        backgroundColor: `${rarity.color}15`,
                        boxShadow: `0 0 12px ${rarity.glow}`
                      }}
                      data-testid={`badge-mini-${b.id}`}>
                      <Icon className="w-5 h-5" style={{ color: b.color || rarity.color }} />
                    </div>
                  );
                })}
                {unlockedBadges.length > 8 && (
                  <div className="w-11 h-11 rounded-lg flex items-center justify-center border border-[#333] bg-[#0a0a0a]">
                    <span className="text-[10px] text-[#888] font-game">+{unlockedBadges.length - 8}</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {TABS.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-lg font-game text-xs whitespace-nowrap transition-all ${
                activeTab === tab.id ? 'bg-[#14F195]/20 border-2 border-[#14F195] text-[#14F195]' : 'bg-[#111] border border-[#222] text-[#888] hover:text-white'
              }`} data-testid={`profile-tab-${tab.id}`}>
              <tab.icon className="w-4 h-4" /> {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <OverviewTab modeStats={mode_stats} recentScores={recent_scores} />
        )}
        {activeTab === 'badges' && (
          <BadgesTab achievements={achievements} unlockedBadges={unlockedBadges} lockedBadges={lockedBadges} />
        )}
        {activeTab === 'games' && (
          <GamesTab recentScores={recent_scores} />
        )}
        {activeTab === 'tournaments' && (
          <TournamentsTab tournamentResults={tournament_results} />
        )}
        {activeTab === 'betting' && (
          <BettingTab betStats={bet_stats} />
        )}
        {activeTab === 'collection' && (
          <CollectionTab ownedNfts={owned_nfts} ownedSkins={owned_skins} />
        )}
      </div>
    </div>
  );
};

export default PlayerProfile;
